import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Common } from 'helpers/common';
import { Loader2 } from 'lucide-react';
import { changePassword, getLoggedUser } from 'helpers/backend_helper';
import { useAuth } from 'hooks/useStore';

const ChangePassword = () => {
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const commonHelper = new Common();
    const { user } = useAuth();

    // validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: Yup.object({
            currentPassword: Yup.string().required("Please enter your current password"),
            newPassword: Yup.string()
                .required("Please enter new password")
                .min(6, "Password must be at least 6 characters")
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
                    "Password must contain at least one uppercase letter, one lowercase letter, one number and one special character"
                ),
            confirmPassword: Yup.string()
                .required("Please confirm your password")
                .oneOf([Yup.ref('newPassword')], "Passwords don't match"),
        }),
        onSubmit: async (values) => {
            setIsSaving(true);
            try {
                const data = {
                    userId: user?.userId,
                    loginId: user?.loginId,
                    currentPassword: values.currentPassword,
                    newPassword: values.newPassword,
                }
                const response = await changePassword(data);   
                commonHelper.showSuccessMessage('Password has been changed successfully.');
                validation.resetForm();
            } catch (error) {
                console.error('Error changing password:', error);
                commonHelper.showErrorMessage(error as string);
            } finally {
                setIsSaving(false);
            }
        },
    });

    return (
        <React.Fragment>
            <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
                <div className="xl:col-span-12">
                    <div className="card">
                        <div className="flex items-center border-y border-dashed" style={{ padding: 10 }}>
                            <h6 className="text-15 grow">Change Password</h6>
                        </div>
                        <div className="card-body grid grid-cols-1 gap-5 xl:grid-cols-6 xl:col-span-6">
                            <form action="#!" onSubmit={(e) => {
                                e.preventDefault();
                                validation.handleSubmit();
                                return false;
                            }}>
                                <div className="grid grid-cols-1 gap-5 xl:grid-cols-6 xl:col-span-6">
                                    <div className="xl:col-span-12">
                                        <label htmlFor="currentPassword" className="inline-block mb-2 text-base font-medium">Current Password</label>
                                        <input
                                            type="password"
                                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                            id="currentPassword"
                                            name="currentPassword"
                                            placeholder="Enter current password"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.currentPassword || ""}
                                        />
                                        {validation.touched.currentPassword && validation.errors.currentPassword ? (
                                            <div className="mt-1 text-sm text-red-500">{validation.errors.currentPassword}</div>
                                        ) : null}
                                    </div>

                                    <div className="xl:col-span-12">
                                        <label htmlFor="newPassword" className="inline-block mb-2 text-base font-medium">New Password</label>
                                        <input
                                            type="password"
                                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                            id="newPassword"
                                            name="newPassword"
                                            placeholder="Enter new password"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.newPassword || ""}
                                        />
                                        {validation.touched.newPassword && validation.errors.newPassword ? (
                                            <div className="mt-1 text-sm text-red-500">{validation.errors.newPassword}</div>
                                        ) : null}
                                    </div>

                                    <div className="xl:col-span-12">
                                        <label htmlFor="confirmPassword" className="inline-block mb-2 text-base font-medium">Confirm Password</label>
                                        <input
                                            type="password"
                                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            placeholder="Confirm new password"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.confirmPassword || ""}
                                        />
                                        {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                                            <div className="mt-1 text-sm text-red-500">{validation.errors.confirmPassword}</div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-4">
                                    {!isSaving ? (
                                        <button
                                            type="submit"
                                            className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                                        >
                                            Change Password
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled
                                            className="flex items-center text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                                        >
                                            <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                                            Changing Password...
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChangePassword; 